import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, MapPin, Clock, Navigation,
  Phone, CreditCard, ArrowUpRight, ArrowDownLeft,
  Milestone, CalendarDays,
  AlertTriangle, ChevronDown, ChevronUp, RefreshCw,
  Info, CheckCircle, User,
} from "lucide-react";

const STATUS = {
  Active:  { bg:"#F0FDF4", color:"#16A34A", border:"#BBF7D0", dot:"#16A34A" },
  Idle:    { bg:"#FFFBEB", color:"#D97706", border:"#FDE68A", dot:"#D97706" },
  Offline: { bg:"#F9FAFB", color:"#6B7280", border:"#E5E7EB", dot:"#9CA3AF" },
};

// Only Entry (E) and Exit (X) are shown — compass directions are not displayed
function laneInfo(dir) {
  const map = {
    E: { label:"Entry", Icon: ArrowDownLeft, color:"#16A34A", bg:"#F0FDF4", border:"#BBF7D0" },
    X: { label:"Exit",  Icon: ArrowUpRight,  color:"#DC2626", bg:"#FEF2F2", border:"#FECACA" },
  };
  return map[dir?.toUpperCase()] || { label: "", Icon: Navigation, color:"#6B7280", bg:"#F9FAFB", border:"#E5E7EB" };
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric", weekday:"short" });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit", hour12:true });
}
function timeAgo(iso) {
  const diff  = Date.now() - new Date(iso).getTime();
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (hours < 1)  return "< 1h ago";
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

/* ── Stat card ─────────────────────────────────────────── */
function StatCard({ label, value, sub, icon:Icon }) {
  return (
    <div style={{
      background:"#ffffff", border:"1px solid #F0F1F3",
      borderRadius:10, padding:"14px 16px",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:6 }}>
        {Icon && <Icon size={10} color="#9CA3AF" />}
        <span style={{ fontSize:11, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.6 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize:22, fontWeight:700, color:"#111827", lineHeight:1, marginBottom:3 }}>{value}</div>
      {sub && <div style={{ fontSize:13, color:"#9CA3AF" }}>{sub}</div>}
    </div>
  );
}


/* ── Location quality card (shown in noRecentData view) ── */
function LocationSourceCard({ vehicle }) {
  if (!vehicle.lat || !vehicle.lng) return null;

  const isRC    = vehicle.isRCLocation;
  const isState = vehicle.isStateLocation;

  const config = isRC
    ? { color:"#2563EB", bg:"#EFF6FF", border:"#BFDBFE", icon:"🏠", label:"RC Registered Address", detail: vehicle.location, quality:"City-level accuracy" }
    : isState
      ? { color:"#D97706", bg:"#FFFBEB", border:"#FDE68A", icon:"~", label:"Registration District Estimate", detail: vehicle.location, quality:"Approximate — based on plate number RTO code" }
      : { color:"#16A34A", bg:"#F0FDF4", border:"#BBF7D0", icon:"📍", label:"Last FASTag Crossing", detail: vehicle.location, quality:"Accurate · toll plaza level" };

  return (
    <div style={{
      background: config.bg, border:`1px solid ${config.border}`,
      borderRadius:10, padding:"12px 14px", marginBottom:12,
      display:"flex", alignItems:"flex-start", gap:10,
    }}>
      <div style={{
        width:32, height:32, borderRadius:8, flexShrink:0,
        background:"rgba(255,255,255,0.7)", border:`1px solid ${config.border}`,
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:15, fontWeight:700, color:config.color,
      }}>{config.icon}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:15, fontWeight:700, color:config.color, textTransform:"uppercase", letterSpacing:0.5, marginBottom:3 }}>
          Location Source
        </div>
        <div style={{ fontSize:14, fontWeight:700, color:config.color, marginBottom:2 }}>{config.label}</div>
        <div style={{ fontSize:15, color:"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:3 }}>
          {config.detail}
        </div>
        <div style={{ fontSize:14, color: isState ? "#D97706" : config.color, fontStyle:"italic" }}>
          {config.quality}
        </div>
        <div style={{ fontSize:14, color:"#9CA3AF", fontFamily:"monospace", marginTop:3 }}>
          {vehicle.lat?.toFixed(5)}°N, {vehicle.lng?.toFixed(5)}°E
        </div>
      </div>
    </div>
  );
}

/* ── No data diagnostic panel ──────────────────────────── */
function NoDataPanel({ vehicle, onRefresh, onSetManualLocation }) {
  const [showReasons,  setShowReasons]  = useState(false);
  const [manualCity,   setManualCity]   = useState("");
  const [settingLoc,   setSettingLoc]   = useState(false);
  const [locFocused,   setLocFocused]   = useState(false);

  async function handleSetLocation(e) {
    e.preventDefault();
    if (!manualCity.trim() || !onSetManualLocation) return;
    setSettingLoc(true);
    const ok = await onSetManualLocation(manualCity.trim());
    if (ok) setManualCity("");
    setSettingLoc(false);
  }

  const REASONS = [
    {
      title: "FASTag data sync delay",
      detail: "NETC (National Electronic Toll Collection) takes 24–72 hours to reflect a new crossing. If you crossed a toll recently, wait and refresh later.",
      type: "wait",
    },
    {
      title: "Cash lane or non-FASTag lane",
      detail: "If you passed through a cash payment lane, the crossing is not recorded in NETC. Only FASTag-dedicated lanes report to NETC.",
      type: "info",
    },
    {
      title: "FASTag not active or blacklisted",
      detail: "Your FASTag might be inactive, blacklisted, or have an insufficient balance. Check your bank's FASTag app for the tag status.",
      type: "warn",
    },
    {
      title: "FASTag linked to a different plate",
      detail: "If your FASTag was registered with a slightly different vehicle number (e.g. with/without spaces), NETC may not match it. Verify in your FASTag account.",
      type: "warn",
    },
    {
      title: "State highway or private toll plaza",
      detail: "Some state roads and private toll plazas do not report to the central NETC network. Only NH (National Highway) tolls are guaranteed to report.",
      type: "info",
    },
  ];

  const typeColor = { wait:"#2563EB", info:"#6B7280", warn:"#D97706" };
  const typeBg    = { wait:"#EFF6FF", info:"#F9FAFB", warn:"#FFFBEB" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

      {/* Main status card */}
      <div style={{
        background:"#FFFBEB",
        border:"1.5px solid #FDE68A",
        borderRadius:12, overflow:"hidden",
      }}>
        <div style={{
          background:"#F59E0B", padding:"10px 14px",
          display:"flex", alignItems:"center", gap:8,
        }}>
          <AlertTriangle size={14} color="#fff" />
          <span style={{ fontSize:15, fontWeight:700, color:"#fff" }}>No FASTag Transactions Found</span>
        </div>
        <div style={{ padding:"14px" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"8px 10px", background:"#fff", borderRadius:8,
              border:"1px solid #FDE68A",
            }}>
              <span style={{ fontSize:15, color:"#92400E", fontWeight:600 }}>Plate searched</span>
              <span style={{ fontSize:14, fontWeight:700, color:"#111827", fontFamily:"monospace" }}>
                {vehicle.plateNumber}
              </span>
            </div>
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"8px 10px", background:"#fff", borderRadius:8,
              border:"1px solid #FDE68A",
            }}>
              <span style={{ fontSize:15, color:"#92400E", fontWeight:600 }}>Data source</span>
              <span style={{ fontSize:15, color:"#374151" }}>NETC via Masters India API</span>
            </div>
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"8px 10px", background:"#fff", borderRadius:8,
              border:"1px solid #FDE68A",
            }}>
              <span style={{ fontSize:15, color:"#92400E", fontWeight:600 }}>Period checked</span>
              <span style={{ fontSize:15, color:"#374151" }}>Last 60 days</span>
            </div>
            <div style={{
              display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"8px 10px", background:"#FEF2F2", borderRadius:8,
              border:"1px solid #FECACA",
            }}>
              <span style={{ fontSize:15, color:"#991B1B", fontWeight:600 }}>Result</span>
              <span style={{ fontSize:15, fontWeight:700, color:"#DC2626" }}>
                0 crossings recorded in NETC
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Why might this happen */}
      <div style={{
        border:"1px solid #F0F1F3", borderRadius:10, overflow:"hidden",
      }}>
        <button
          onClick={() => setShowReasons(v => !v)}
          style={{
            width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"10px 14px", background:"#F9FAFB", border:"none", cursor:"pointer",
          }}
        >
          <div style={{ display:"flex", alignItems:"center", gap:7 }}>
            <Info size={12} color="#6B7280" />
            <span style={{ fontSize:14, fontWeight:600, color:"#374151" }}>
              Why might this happen?
            </span>
          </div>
          {showReasons
            ? <ChevronUp size={14} color="#9CA3AF" />
            : <ChevronDown size={14} color="#9CA3AF" />}
        </button>
        <AnimatePresence>
          {showReasons && (
            <motion.div
              initial={{ height:0, opacity:0 }}
              animate={{ height:"auto", opacity:1 }}
              exit={{ height:0, opacity:0 }}
              transition={{ duration:0.2 }}
              style={{ overflow:"hidden" }}
            >
              <div style={{ padding:"0 14px 14px", display:"flex", flexDirection:"column", gap:8 }}>
                {REASONS.map((r, i) => (
                  <div key={i} style={{
                    background: typeBg[r.type],
                    borderRadius:8, padding:"8px 10px",
                    borderLeft:`3px solid ${typeColor[r.type]}`,
                  }}>
                    <div style={{ fontSize:15, fontWeight:700, color: typeColor[r.type], marginBottom:3 }}>
                      {r.title}
                    </div>
                    <div style={{ fontSize:14, color:"#6B7280", lineHeight:1.5 }}>
                      {r.detail}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* What to do */}
      <div style={{
        background:"#F0FDF4", border:"1px solid #BBF7D0",
        borderRadius:10, padding:"12px 14px",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
          <CheckCircle size={12} color="#16A34A" />
          <span style={{ fontSize:15, fontWeight:700, color:"#15803D" }}>Steps to verify</span>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
          <div style={{ fontSize:15, color:"#374151", lineHeight:1.5 }}>
            <strong style={{ color:"#15803D" }}>1.</strong> Open your bank&apos;s FASTag app — check if the crossing appears there.
            If it does, NETC sync is still pending (wait 24–72h then Refresh).
          </div>
          <div style={{ fontSize:15, color:"#374151", lineHeight:1.5 }}>
            <strong style={{ color:"#15803D" }}>2.</strong> Check{" "}
            <span style={{ color:"#2563EB", fontWeight:600 }}>fastag.ihmcl.com</span>{" "}
            — the government NETC portal. If it&apos;s not there either, the FASTag is not active on NETC.
          </div>
          <div style={{ fontSize:15, color:"#374151", lineHeight:1.5 }}>
            <strong style={{ color:"#15803D" }}>3.</strong> Verify the plate number entered matches exactly what&apos;s registered in your FASTag account.
          </div>
        </div>
      </div>

      {/* Refresh button */}
      {onRefresh && (
        <button
          onClick={onRefresh}
          style={{
            width:"100%", padding:"10px 0",
            background:"#2563EB", border:"none", borderRadius:8,
            color:"#fff", fontSize:15, fontWeight:600, cursor:"pointer",
            display:"flex", alignItems:"center", justifyContent:"center", gap:7,
            boxShadow:"0 2px 8px rgba(37,99,235,0.25)",
            transition:"background 0.15s",
          }}
          onMouseOver={e => e.currentTarget.style.background="#1D4ED8"}
          onMouseOut={e  => e.currentTarget.style.background="#2563EB"}
        >
          <RefreshCw size={13} /> Re-check FASTag Data Now
        </button>
      )}

      {/* Manual location — fallback when FASTag has no data */}
      {onSetManualLocation && (
        <div style={{
          border:"1px solid #E0E7FF", borderRadius:10,
          overflow:"hidden", background:"#F5F7FF",
        }}>
          <div style={{
            padding:"10px 14px", borderBottom:"1px solid #E0E7FF",
            display:"flex", alignItems:"center", gap:7,
          }}>
            <MapPin size={12} color="#4F46E5" />
            <span style={{ fontSize:14, fontWeight:700, color:"#3730A3" }}>
              Set location manually
            </span>
            <span style={{ fontSize:12, color:"#6B7280", marginLeft:"auto" }}>
              FASTag unavailable
            </span>
          </div>
          <form onSubmit={handleSetLocation} style={{ padding:"12px 14px", display:"flex", flexDirection:"column", gap:8 }}>
            <span style={{ fontSize:13, color:"#4B5563", lineHeight:1.5 }}>
              Type a city or highway name to pin this vehicle on the map.
            </span>
            <div style={{ display:"flex", gap:8 }}>
              <input
                value={manualCity}
                onChange={e => setManualCity(e.target.value)}
                placeholder="e.g. Vadodara, NH48, Jaipur…"
                disabled={settingLoc}
                onFocus={() => setLocFocused(true)}
                onBlur={()  => setLocFocused(false)}
                style={{
                  flex:1, height:38, padding:"0 10px",
                  border:`1.5px solid ${locFocused ? "#4F46E5" : "#D1D5DB"}`,
                  borderRadius:7, fontSize:14, outline:"none",
                  background:"#fff", color:"#111827",
                  transition:"border-color 0.15s",
                }}
              />
              <button
                type="submit"
                disabled={!manualCity.trim() || settingLoc}
                style={{
                  padding:"0 14px", height:38,
                  background: (!manualCity.trim() || settingLoc) ? "#A5B4FC" : "#4F46E5",
                  border:"none", borderRadius:7,
                  color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer",
                  display:"flex", alignItems:"center", gap:5,
                  transition:"background 0.15s", flexShrink:0,
                }}
              >
                {settingLoc ? <RefreshCw size={12} style={{ animation:"spin 1s linear infinite" }} /> : <MapPin size={12} />}
                {settingLoc ? "Setting…" : "Set"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/* ── Selected toll detail card ─────────────────────────── */
function ActiveTollCard({ toll, onClear }) {
  const lane = laneInfo(toll.direction || toll.location);
  const LaneIcon = lane.Icon;

  return (
    <motion.div
      initial={{ opacity:0, y:-8, scale:.98 }}
      animate={{ opacity:1, y:0,  scale:1   }}
      exit={{    opacity:0, y:-8, scale:.98 }}
      transition={{ duration:0.18, ease:"easeOut" }}
      style={{
        margin:"0 0 16px",
        background:"#ffffff",
        border:"1.5px solid #2563EB",
        borderRadius:12,
        overflow:"hidden",
        boxShadow:"0 4px 20px rgba(37,99,235,0.12)",
      }}
    >
      {/* Blue top bar */}
      <div style={{
        background:"#2563EB", padding:"10px 14px",
        display:"flex", alignItems:"center", justifyContent:"space-between",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:7 }}>
          <div style={{
            width:22, height:22, borderRadius:"50%",
            background:"rgba(255,255,255,0.2)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:15, fontWeight:700, color:"#fff",
          }}>₹</div>
          <span style={{ fontSize:15, fontWeight:700, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:190 }}>
            {toll.plaza}
          </span>
        </div>
        <button
          onClick={onClear}
          style={{
            background:"rgba(255,255,255,0.15)", border:"none",
            borderRadius:6, width:24, height:24, cursor:"pointer",
            color:"#fff", fontSize:14, display:"flex", alignItems:"center", justifyContent:"center",
          }}
        >×</button>
      </div>

      <div style={{ padding:"12px 14px", display:"flex", flexDirection:"column", gap:8 }}>
        {/* Time */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <Clock size={11} color="#9CA3AF" />
            <span style={{ fontSize:15, color:"#6B7280" }}>Crossed at</span>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#111827" }}>{fmtTime(toll.timestamp)}</div>
            <div style={{ fontSize:14, color:"#9CA3AF" }}>{fmtDate(toll.timestamp)}</div>
          </div>
        </div>

        {/* Direction */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <Navigation size={11} color="#9CA3AF" />
            <span style={{ fontSize:15, color:"#6B7280" }}>Lane direction</span>
          </div>
          <div style={{
            display:"flex", alignItems:"center", gap:5,
            background:lane.bg, border:`1px solid ${lane.border}`,
            borderRadius:99, padding:"3px 10px",
          }}>
            <LaneIcon size={10} color={lane.color} />
            <span style={{ fontSize:15, fontWeight:600, color:lane.color }}>{lane.label}</span>
          </div>
        </div>

        {/* Coordinates */}
        {toll.lat && toll.lng && (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <MapPin size={11} color="#9CA3AF" />
              <span style={{ fontSize:15, color:"#6B7280" }}>Coordinates</span>
            </div>
            <span style={{ fontSize:14, fontFamily:"monospace", color:"#374151" }}>
              {toll.lat.toFixed(5)}, {toll.lng.toFixed(5)}
            </span>
          </div>
        )}

        <div style={{ fontSize:14, color:"#9CA3AF", textAlign:"right" }}>
          {timeAgo(toll.timestamp)}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Timeline grouped by date ──────────────────────────── */
function TollTimeline({ history, onTollClick, activeTollId }) {
  const activeRef = useRef(null);

  // Scroll to active toll when it changes
  useEffect(() => {
    if (activeTollId && activeRef.current) {
      activeRef.current.scrollIntoView({ behavior:"smooth", block:"nearest" });
    }
  }, [activeTollId]);

  if (!history?.length) return null;

  // Group by calendar date (newest first — history[0] is most recent)
  const groups = {};
  history.forEach(toll => {
    const dateKey = new Date(toll.timestamp).toDateString();
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(toll);
  });

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {Object.entries(groups).map(([dateKey, tolls]) => (
        <div key={dateKey}>
          {/* Date header */}
          <div style={{
            display:"flex", alignItems:"center", gap:8, marginBottom:12,
          }}>
            <CalendarDays size={11} color="#9CA3AF" />
            <span style={{ fontSize:14, fontWeight:700, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.5 }}>
              {new Date(dateKey).toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"short" })}
            </span>
            <div style={{ flex:1, height:1, background:"#F3F4F6" }} />
            <span style={{ fontSize:14, color:"#D1D5DB" }}>{tolls.length} crossing{tolls.length > 1 ? "s" : ""}</span>
          </div>

          {/* Toll items */}
          <div style={{ position:"relative" }}>
            <div style={{
              position:"absolute", left:10, top:6, bottom:6,
              width:1.5, background:"#F3F4F6", borderRadius:99,
            }} />
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {tolls.map(toll => {
                const active = activeTollId === toll.id;
                const hasGeo = toll.lat && toll.lng;
                const lane   = laneInfo(toll.direction || toll.location);
                const LaneIcon = lane.Icon;

                return (
                  <div
                    key={toll.id}
                    ref={active ? activeRef : null}
                    onClick={() => hasGeo && onTollClick?.(toll)}
                    style={{
                      display:"flex", gap:14,
                      cursor: hasGeo ? "pointer" : "default",
                      padding:"8px 10px 8px 0",
                      borderRadius:8,
                      background: active ? "#EFF6FF" : "transparent",
                      border: active ? "1px solid #BFDBFE" : "1px solid transparent",
                      transition:"all 0.15s",
                    }}
                    onMouseOver={e => {
                      if (!active && hasGeo) e.currentTarget.style.background = "#F9FAFB";
                    }}
                    onMouseOut={e => {
                      if (!active) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {/* Timeline dot */}
                    <div style={{ paddingLeft:4, display:"flex", flexDirection:"column", alignItems:"center" }}>
                      <div style={{
                        width:17, height:17, borderRadius:"50%", flexShrink:0, marginTop:2,
                        background: active ? "#2563EB" : "#ffffff",
                        border:`2px solid ${active ? "#2563EB" : "#D1D5DB"}`,
                        zIndex:1, position:"relative",
                        boxShadow: active ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
                        transition:"all 0.15s",
                      }} />
                    </div>

                    <div style={{ flex:1, minWidth:0 }}>
                      {/* Plaza name + time */}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8 }}>
                        <span style={{
                          fontSize:14, fontWeight:600,
                          color: active ? "#1D4ED8" : "#111827",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                          flex:1,
                        }}>
                          {toll.plaza}
                        </span>
                        <span style={{ fontSize:14, color:"#9CA3AF", flexShrink:0, marginTop:1 }}>
                          {fmtTime(toll.timestamp)}
                        </span>
                      </div>

                      {/* Entry/Exit badge — compass dirs (N/S/W) are hidden */}
                      <div style={{ marginTop:5, display:"flex", alignItems:"center", gap:6 }}>
                        {(toll.direction === "E" || toll.direction === "X") && (
                          <div style={{
                            display:"inline-flex", alignItems:"center", gap:4,
                            background:lane.bg, border:`1px solid ${lane.border}`,
                            borderRadius:99, padding:"2px 7px",
                          }}>
                            <LaneIcon size={8} color={lane.color} />
                            <span style={{ fontSize:11, fontWeight:600, color:lane.color }}>{lane.label}</span>
                          </div>
                        )}
                        {hasGeo && !active && (
                          <span style={{ fontSize:11, color:"#D1D5DB" }}>Click to locate on map</span>
                        )}
                        {active && (
                          <span style={{ fontSize:11, color:"#2563EB", fontWeight:500 }}>Shown on map</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* Journey start cap */}
      <div style={{ display:"flex", alignItems:"center", gap:10, paddingLeft:4 }}>
        <div style={{
          width:17, height:17, borderRadius:"50%", flexShrink:0,
          background:"#16A34A", border:"2px solid #16A34A",
        }} />
        <div>
          <div style={{ fontSize:14, fontWeight:600, color:"#16A34A" }}>Journey Started</div>
          <div style={{ fontSize:14, color:"#9CA3AF", marginTop:1 }}>
            {fmtDate(history[history.length - 1].timestamp)}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Cache status bar ──────────────────────────────────── */
function CacheStatusBar({ vehicle, onForceRefresh }) {
  const ts = vehicle.lastFastagFetch;
  if (!ts) return null;

  const ageMs  = Date.now() - ts;
  const ageMin = Math.floor(ageMs / 60000);
  const ageH   = Math.floor(ageMin / 60);
  const ageM   = ageMin % 60;
  const ageStr = ageH > 0 ? `${ageH}h ${ageM}m ago` : ageMin < 1 ? "just now" : `${ageMin}m ago`;

  const remMs  = vehicle.nextFetchAt ? Math.max(0, vehicle.nextFetchAt - Date.now()) : 0;
  const ready  = remMs <= 0;
  const remMin = Math.ceil(remMs / 60000);
  const remH   = Math.floor(remMin / 60);
  const remM   = remMin % 60;
  const remStr = ready ? "now" : remH > 0 ? `${remH}h ${remM}m` : `${remMin}m`;

  return (
    <div style={{
      display:"flex", alignItems:"center", gap:8,
      background: ready ? "#F0FDF4" : "#EFF6FF",
      border: `1px solid ${ready ? "#BBF7D0" : "#BFDBFE"}`,
      borderRadius:8, padding:"8px 12px", marginBottom:12,
    }}>
      <span style={{ fontSize:14, color: ready ? "#16A34A" : "#1E40AF", flex:1 }}>
        <span style={{ fontWeight:700 }}>Cached</span>
        {" · "}checked {ageStr}
        {!ready && <> · auto-refresh in <strong>{remStr}</strong></>}
        {ready  && <> · <strong>refresh due</strong></>}
      </span>
      <button
        onClick={onForceRefresh}
        style={{
          background:"none", border:"none", cursor:"pointer",
          fontSize:14, fontWeight:700,
          color: ready ? "#15803D" : "#2563EB",
          padding:0, whiteSpace:"nowrap",
        }}
      >
        Refresh Now →
      </button>
    </div>
  );
}

/* ── Estimated position card ───────────────────────────── */
function EstimatedPositionCard({ estimatedPos }) {
  if (!estimatedPos) return null;
  const { distKm, lastTollName, elapsedH, direction } = estimatedPos;
  const hours = Math.floor(elapsedH);
  const mins  = Math.round((elapsedH - hours) * 60);
  const dirLabels = { N:"Northbound", S:"Southbound", W:"Westbound", NE:"NE-bound", NW:"NW-bound", E:"moving", X:"moving" };
  const dirStr    = dirLabels[direction?.toUpperCase()] || "moving";

  return (
    <div style={{
      background:"#FFFBEB", border:"1.5px solid #FDE68A",
      borderRadius:10, padding:"12px 14px", marginBottom:16,
    }}>
      <div style={{
        display:"flex", alignItems:"center", gap:7, marginBottom:8,
      }}>
        <div style={{
          fontSize:15, fontWeight:700, color:"#D97706",
          background:"rgba(217,119,6,0.1)", border:"1px solid #FDE68A",
          borderRadius:4, padding:"2px 6px", letterSpacing:0.3,
        }}>
          ~ ESTIMATED POSITION
        </div>
      </div>
      <div style={{ fontSize:15, fontWeight:700, color:"#92400E", marginBottom:4 }}>
        ~{distKm} km from {lastTollName || "last toll"}
      </div>
      <div style={{ fontSize:15, color:"#D97706", lineHeight:1.5 }}>
        Vehicle has been {dirStr} for {hours > 0 ? `${hours}h ${mins}m` : `${mins}m`}.
        Position is estimated based on average highway speed — no GPS available between tolls.
      </div>
      <div style={{ fontSize:14, color:"#B45309", marginTop:6, fontStyle:"italic" }}>
        Exact location resumes when the vehicle crosses the next toll plaza.
      </div>
    </div>
  );
}

/* ── Main panel ────────────────────────────────────────── */
export default function VehicleDetail({ vehicle, onBack, onTollClick, activeTollId, onRefresh, estimatedPos, onForceRefresh, onSetManualLocation }) {
  const s          = STATUS[vehicle.status] || STATUS.Offline;
  const tollCount  = vehicle.tollHistory?.length || 0;
  const firstToll  = tollCount ? vehicle.tollHistory[tollCount - 1] : null;
  const lastToll   = tollCount ? vehicle.tollHistory[0]             : null;
  const activeToll = activeTollId
    ? vehicle.tollHistory?.find(t => t.id === activeTollId)
    : null;

  useEffect(() => {
    const h = e => { if (e.key === "Escape") onBack?.(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onBack]);

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{
        padding:"18px 18px",
        borderBottom:"1px solid #F5F6F7",
        flexShrink:0,
        background:"#ffffff",
      }}>
        <button
          onClick={onBack}
          style={{
            display:"flex", alignItems:"center", gap:6,
            background:"none", border:"none", cursor:"pointer",
            color:"#6B7280", fontSize:14, fontWeight:500, padding:"0 0 14px",
            transition:"color 0.15s",
          }}
          onMouseOver={e => e.currentTarget.style.color="#2563EB"}
          onMouseOut={e  => e.currentTarget.style.color="#6B7280"}
        >
          <ArrowLeft size={13} /> Back to Fleet
        </button>

        {/* Plate + status */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
          <span style={{
            fontFamily:"'SF Mono','Fira Code',monospace",
            fontSize:13, fontWeight:700, letterSpacing:"0.07em",
            color:"#111827", background:"#F3F4F6",
            border:"1px solid #E5E7EB", borderRadius:6,
            padding:"4px 10px", whiteSpace:"nowrap",
          }}>{vehicle.plateNumber}</span>
          <div style={{
            display:"flex", alignItems:"center", gap:5,
            background:s.bg, border:`1px solid ${s.border}`,
            borderRadius:99, padding:"4px 10px",
          }}>
            <div style={{
              width:6, height:6, borderRadius:"50%", background:s.dot,
              ...(vehicle.isTracking ? { animation:"pulse 1.5s infinite" } : {}),
            }} />
            <span style={{ fontSize:15, fontWeight:700, color:s.color }}>
              {vehicle.isTracking ? "LIVE" : vehicle.status}
            </span>
          </div>
        </div>

        {/* Nickname + type */}
        <div style={{ marginBottom:6 }}>
          <div style={{ fontSize:19, fontWeight:700, color:"#111827" }}>{vehicle.nickname}</div>
          <div style={{ fontSize:14, color:"#9CA3AF", marginTop:1 }}>
            {vehicle.type}
            {vehicle.ownerName ? ` · ${vehicle.ownerName}` : ""}
          </div>
        </div>

        {/* Assigned driver */}
        {vehicle.driverId && (
          <div style={{
            display:"flex", alignItems:"center", gap:8,
            background:"#F5F3FF", border:"1px solid #DDD6FE",
            borderRadius:8, padding:"7px 10px", marginBottom:8,
          }}>
            <User size={12} color="#7C3AED" />
            <span style={{ fontSize:13, fontWeight:700, color:"#374151" }}>{vehicle.driverName}</span>
            {vehicle.driverPhone && (
              <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, color:"#6B7280", marginLeft:"auto" }}>
                <Phone size={10} color="#9CA3AF" /> {vehicle.driverPhone}
              </span>
            )}
          </div>
        )}

        {/* Contact + FASTag ID */}
        {(vehicle.contactNumber || vehicle.fastagId) && (
          <div style={{ display:"flex", gap:12, marginTop:4 }}>
            {vehicle.contactNumber && (
              <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:15, color:"#6B7280" }}>
                <Phone size={10} color="#9CA3AF" /> {vehicle.contactNumber}
              </div>
            )}
            {vehicle.fastagId && (
              <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:15, color:"#6B7280" }}>
                <CreditCard size={10} color="#9CA3AF" /> {vehicle.fastagId}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Scrollable body ─────────────────────────────── */}
      <div style={{ flex:1, overflowY:"auto", padding:"18px 18px 28px" }}>

        {vehicle.noRecentData && !vehicle.tollHistory?.length ? (
          /* ── No FASTag data state — no cached history either ── */
          <>
            <LocationSourceCard vehicle={vehicle} />
            <NoDataPanel
              vehicle={vehicle}
              onRefresh={onForceRefresh ? () => onForceRefresh(vehicle.id) : (onRefresh ? () => onRefresh(vehicle.id) : null)}
              onSetManualLocation={onSetManualLocation ? city => onSetManualLocation(vehicle.id, city) : null}
            />
          </>
        ) : (
          <>
            {/* Stale data banner — shown when API returned no new data but cached history exists */}
            {vehicle.noRecentData && vehicle.tollHistory?.length > 0 && (
              <div style={{
                display:"flex", alignItems:"center", justifyContent:"space-between",
                background:"#FFFBEB", border:"1px solid #FDE68A",
                borderRadius:8, padding:"8px 12px", marginBottom:10, gap:8,
              }}>
                <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                  <Clock size={12} color="#D97706" style={{ flexShrink:0 }} />
                  <span style={{ fontSize:12, color:"#92400E", fontWeight:500 }}>
                    No new crossings detected — showing last known data
                  </span>
                </div>
                {(onForceRefresh || onRefresh) && (
                  <button
                    onClick={() => onForceRefresh ? onForceRefresh(vehicle.id) : onRefresh(vehicle.id)}
                    style={{
                      background:"none", border:"none", cursor:"pointer",
                      fontSize:12, fontWeight:600, color:"#D97706", flexShrink:0, padding:0,
                    }}
                  >Refresh →</button>
                )}
              </div>
            )}

            {/* Cache / next-refresh status */}
            {!vehicle.noRecentData && (
              <CacheStatusBar
                vehicle={vehicle}
                onForceRefresh={onForceRefresh ? () => onForceRefresh(vehicle.id) : () => onRefresh?.(vehicle.id)}
              />
            )}

            {/* Estimated position — shown between toll crossings */}
            <EstimatedPositionCard estimatedPos={estimatedPos} />

            {/* Stats */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
              <StatCard
                label="Total Tolls" icon={Milestone}
                value={tollCount}
                sub="crossings in last 60 days"
              />
              <StatCard
                label="Vehicle Type" icon={Navigation}
                value={vehicle.type}
                sub={`Reg: ${vehicle.plateNumber}`}
              />
              <StatCard
                label="First Check-in" icon={CalendarDays}
                value={firstToll ? fmtTime(firstToll.timestamp) : "—"}
                sub={firstToll ? fmtDate(firstToll.timestamp) : "No data"}
              />
              <StatCard
                label="Last Crossing" icon={Clock}
                value={lastToll ? fmtTime(lastToll.timestamp) : "—"}
                sub={lastToll ? timeAgo(lastToll.timestamp) : "No data"}
              />
            </div>

            {/* Current location */}
            <div style={{
              background:"#ffffff",
              border:"1px solid #E5E7EB",
              borderRadius:12, marginBottom:16,
              overflow:"hidden",
              boxShadow:"0 2px 8px rgba(0,0,0,0.06)",
            }}>
              {/* Last known row */}
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px" }}>
                <div style={{
                  width:38, height:38, borderRadius:10, flexShrink:0,
                  background:"#EFF6FF",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <MapPin size={17} color="#2563EB" />
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#2563EB", textTransform:"uppercase", letterSpacing:1, marginBottom:2 }}>
                    Last Known Location
                  </div>
                  <div style={{ fontSize:14, fontWeight:700, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {vehicle.location}
                  </div>
                  {vehicle.lat && vehicle.lng && (
                    <div style={{ fontSize:11, color:"#9CA3AF", fontFamily:"monospace", marginTop:1 }}>
                      {vehicle.lat.toFixed(5)}°N,&nbsp;{vehicle.lng.toFixed(5)}°E
                    </div>
                  )}
                </div>
              </div>

              {/* Origin row — only when different from last known */}
              {vehicle.startLocation && vehicle.startLocation !== vehicle.location && (
                <>
                  <div style={{ height:1, background:"#F3F4F6", margin:"0 14px" }} />
                  <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px" }}>
                    <div style={{
                      width:38, height:38, borderRadius:10, flexShrink:0,
                      background:"#F0FDF4",
                      display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      <Navigation size={15} color="#16A34A" />
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:"#16A34A", textTransform:"uppercase", letterSpacing:1, marginBottom:2 }}>
                        Journey Origin
                      </div>
                      <div style={{ fontSize:13, fontWeight:600, color:"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {vehicle.startLocation}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Active toll detail — shown when a toll is clicked on map or timeline */}
            <AnimatePresence>
              {activeToll && (
                <ActiveTollCard
                  key={activeToll.id}
                  toll={activeToll}
                  onClear={() => onTollClick?.(activeToll)}
                />
              )}
            </AnimatePresence>

            {/* Timeline */}
            <div style={{ marginBottom:4 }}>
              <div style={{
                display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14,
              }}>
                <span style={{ fontSize:15, fontWeight:700, color:"#374151", textTransform:"uppercase", letterSpacing:0.5 }}>
                  Journey Timeline
                </span>
                <span style={{
                  fontSize:14, fontWeight:600, color:"#7C3AED",
                  background:"#F5F3FF", border:"1px solid #DDD6FE",
                  borderRadius:99, padding:"2px 8px",
                }}>
                  {tollCount} toll{tollCount !== 1 ? "s" : ""}
                </span>
              </div>
              <TollTimeline
                history={vehicle.tollHistory}
                onTollClick={onTollClick}
                activeTollId={activeTollId}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
